'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section
      className={`relative flex min-h-screen items-center overflow-hidden ${className}`}
      style={{ backgroundImage: 'var(--gradient-app)' }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 h-96 w-96 rounded-full blur-3xl animate-pulse" style={{ background: 'rgba(85, 214, 255, 0.24)' }} />
        <div className="absolute bottom-20 right-10 h-[500px] w-[500px] rounded-full blur-3xl animate-pulse" style={{ background: 'rgba(0, 209, 160, 0.18)', animationDelay: '700ms' }} />
        <div className="absolute top-1/2 left-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 transform rounded-full blur-3xl animate-pulse" style={{ background: 'rgba(50, 92, 255, 0.16)', animationDelay: '1400ms' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <div className="section-eyebrow bg-white/12 text-white shadow-lg backdrop-blur-md">
              <Icon name="SparklesIcon" size={20} className="text-cyan-200" />
              <span className="text-sm font-semibold text-white">
                Coach Empresarial con Inteligencia Artificial
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-tight">
              Transforma tu Negocio con{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #8be7ff 0%, #59c3ff 46%, #8fffd9 100%)' }}
              >
                LynMet
              </span>
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-white/95 leading-relaxed max-w-2xl">
              Plataforma de coaching empresarial con IA para emprendedores y startups que necesitan analizar su negocio, detectar oportunidades de crecimiento y avanzar con estrategia, foco y próximos pasos concretos.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                href="/start"
                className="btn-primary-app group px-8 py-4 text-lg shadow-2xl hover:scale-105">
                <span>Comenzar Gratis</span>
                <Icon name="ArrowRightIcon" size={22} className="group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                href="/pricing"
                className="btn-secondary-app-dark hidden px-8 py-4 text-lg lg:inline-flex">
                <Icon name="CurrencyDollarIcon" size={22} />
                <span>Ver Precios</span>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm text-white/90">
              <Link href="/coaches" className="rounded-full border border-white/20 bg-white/8 px-4 py-2 hover:bg-white/14 transition-colors">
                Mentores con IA
              </Link>
              <Link href="/pricing" className="rounded-full border border-white/20 bg-white/8 px-4 py-2 hover:bg-white/14 transition-colors">
                Plan gratuito y precios
              </Link>
              <Link href="/about-us" className="rounded-full border border-white/20 bg-white/8 px-4 py-2 hover:bg-white/14 transition-colors">
                Cómo funciona
              </Link>
            </div>

            {/* Trust copy */}
            {isHydrated &&
            <div className="pt-6">
                <div className="space-y-3 max-w-md">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="BoltIcon" size={20} className="text-yellow-300" />
                    </div>
                    <span className="text-white/95 font-medium">Sistema de tokens simple y transparente</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="ArrowPathIcon" size={20} className="text-blue-300" />
                    </div>
                    <span className="text-white/95 font-medium">Recarga semanal cuando realmente la necesitas</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="CheckCircleIcon" size={20} className="text-green-300" />
                    </div>
                    <span className="text-white/95 font-medium">Sin contratos largos ni compromisos innecesarios</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <Icon name="BriefcaseIcon" size={20} className="text-cyan-200" />
                    </div>
                    <span className="text-white/95 font-medium">Diagnóstico de negocio, ideación y estrategia en una sola plataforma</span>
                  </div>
                </div>
              </div>
            }
          </div>

          {/* Right visual */}
          <div className="relative hidden lg:block">
            <div className="relative w-full h-[600px] overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/24">
              <AppImage
                src="/assets/images/Landing Lynmet.png"
                alt="Emprendedor trabajando feliz con LynMet en su laptop"
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
                priority />

              <div className="absolute inset-0 bg-gradient-to-t from-[#081a3a]/74 via-[#0f3f87]/20 to-transparent" />
            </div>

            {/* Floating success card */}
            <div className="absolute -bottom-8 -left-8 max-w-sm rounded-3xl border border-white/70 p-6 shadow-[0_28px_80px_-32px_rgba(8,26,58,0.55)] backdrop-blur-xl" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(238,247,251,0.96) 48%, rgba(224,253,246,0.96) 100%)' }}>
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl shadow-lg" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
                  <Icon name="ChartBarIcon" size={26} className="text-[#08223f]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--primary)]">Resultado inmediato</p>
                  <p className="mt-2 font-bold text-slate-900 text-xl leading-tight">Analiza tu negocio con claridad, foco y próximos pasos concretos.</p>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[rgba(44,120,251,0.08)] px-3 py-1.5 text-xs font-semibold text-[var(--primary-strong)]">
                    <Icon name="ClockIcon" size={14} />
                    Menos fricción, más avance real
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;
